import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { first, switchMap } from 'rxjs/operators';
import { LogService } from '@viag/ngx-logger';

import { GpsService, StorageService } from '@shared/services';
import { Guid, TimeUtil } from '@shared/utils';
import { Hour } from '../types/hour';

@Injectable({
  providedIn: 'root'
})
export class ClockService {
  private storage = inject(StorageService);
  private log = inject(LogService);
  private gps = inject(GpsService);

  private currentSubject$ = new BehaviorSubject<Hour>(null);
  public current$ = this.currentSubject$.asObservable();

  private startedSubject$ = new Subject<Hour>();
  public started$ = this.startedSubject$.asObservable();

  private stopedSubject$ = new Subject<Hour>();
  public stoped$ = this.stopedSubject$.asObservable();

  private changedSubject$ = new Subject<Hour[]>();
  public changed$ = this.changedSubject$.asObservable();

  constructor() {
    this.loadStarted();
  }

  start(projectTaskId, taskName, projectNumber, taskTypeCode: TaskType, journeyType) {
    return this.currentSubject$.pipe(
      first(),
      switchMap(currentStarted => {
        if (currentStarted) {
          return this.stopStarted(currentStarted).then(updatedStarted => {
            this.stopedSubject$.next(updatedStarted);
            return this.startStarted(projectTaskId, taskName, projectNumber, updatedStarted, taskTypeCode, journeyType);
          });
        }
        return this.startStarted(projectTaskId, taskName, projectNumber, currentStarted, taskTypeCode, journeyType);
      }),
    );
  }

  stop() {
    return this.currentSubject$.pipe(
      first(),
      switchMap(currentStarted => {
        if (currentStarted) {
          return this.stopStarted(currentStarted).then(updatedStarted => {
            this.changedSubject$.next([updatedStarted]);
            this.stopedSubject$.next(updatedStarted);
            return this.saveStarted(null);
          });
        }
        return this.saveStarted(null);
      }),
    );
  }

  private createStarted(projectTaskId, taskName, projectNumber, taskTypeCode, journeyType): Promise<Hour> {
    const startTime = new Date();
    this.logClock('Started', projectTaskId, startTime);
    return this.gps.getCoords().then(gpsData => {
      return {
        projectTaskHourId: Guid.uuidv4(),
        projectTaskId,
        projectNumber,
        taskName,
        startTime,
        endTime: null,
        duration: null,
        gpsData,
        taskTypeCode,
        journeyType,
        synced: null,
      };
    });
  }

  private startStarted(projectTaskId, taskName, projectNumber, currentStarted, taskTypeCode, journeyType) {
    return this.createStarted(projectTaskId, taskName, projectNumber, taskTypeCode, journeyType).then(newStarted => {
      return this.saveStarted(newStarted).then(() => {
        const changes = currentStarted
          ? [currentStarted, newStarted]
          : [newStarted];
        this.changedSubject$.next(changes);
        this.startedSubject$.next(newStarted);
        return newStarted;
      });
    });
  }

  private stopStarted(started: Hour): Promise<Hour> {
    const endTime = new Date();
    const duration = TimeUtil.totalHours(started.startTime, endTime);
    this.logClock('Stopped', started.projectTaskId, endTime, duration);
    started.endTime = endTime;
    started.duration = duration;
    return this.gps.getCoords().then(gpsData => {
      started.gpsData = this.appendGps(started.gpsData, gpsData);
      return started;
    });
  }

  private saveStarted(started: Hour) {
    return this.storage.set('started', started).then(() => {
      this.currentSubject$.next(started);
      return started;
    });
  }

  private loadStarted() {
    return this.storage.get('started').then(started => {
      if (started) {
        this.currentSubject$.next(started);
      }
    });
  }

  private appendGps(startPos, endPos) {
    return endPos
      ? (startPos || '') + '/' + endPos
      : startPos;
  }

  private logClock(action, projectTaskId, time, duration?) {
    const id = projectTaskId.substring(0, 6); // abgeschnitten ist einfacher zum lesen im debug aber im prod?
    const localTime = time.toLocaleTimeString();
    let msg = `${action}: ${id} ${localTime}`;
    msg += duration
      ? ` (${duration})`
      : '';
    this.log.debug(msg);
  }

}
